/**
 * VTSectionBlock, Vogue / Tatler-inspired editorial section.
 *
 * The opposite philosophy from EditorialSectionBlock:
 *   • Image is clean, no gradient, no overlay text
 *   • Headline lives BELOW the image, in its own white-space chamber
 *   • Type is restrained (not 100px italic; ~32-60px)
 *   • Body is sans-serif; only headings stay serif
 *   • Generous whitespace, narrow text column (~640px)
 *   • Thin gold rule under the title, the only ornament
 *
 * Where EditorialSection feels like a magazine COVER, VTSection feels like
 * the inside FEATURE spread, quiet, considered, image-led.
 *
 * Variants (block.variant):
 *   • centered     , image centered, eyebrow/title/standfirst stacked
 *                     below in a centered narrow column. Default.
 *   • image-led    , image full viewport width, text in a narrow column
 *                     below. Cleaner version of fullbleed-opener.
 *   • split-clean  , image left (50%), title + text right (50%), no
 *                     sticky, no gradient. Restrained editorial split.
 *
 * Data shape:
 *   {
 *     type: 'vt_section',
 *     variant: 'centered',
 *     image:    { src, alt, focal, caption, credit, crop },
 *     eyebrow:  'Style',
 *     title:    'A Quiet Revolution',
 *     standfirst: 'Optional intro line.',
 *     body: { blocks: [ ... ] }    // any block from the article system
 *   }
 */
import { useRef, useEffect, useState } from 'react';
import { imgUrl } from '../../utils/imgUrl.js';

const GOLD = '#c9a96e';
const FD = "var(--font-heading-primary, 'Cormorant Garamond', Georgia, serif)";
const FU = "var(--font-body, 'Jost', sans-serif)";
const FS = "var(--font-body, 'Jost', sans-serif)";

// ─── Fade-in on enter viewport (same pattern as EditorialSection) ───────────
function useFadeIn(reduced = false) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(reduced);
  useEffect(() => {
    if (reduced) { setVisible(true); return; }
    if (!ref.current || typeof IntersectionObserver === 'undefined') {
      setVisible(true); return;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) { setVisible(true); io.unobserve(e.target); } }),
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [reduced]);
  return [ref, visible];
}

// ─── Header block, eyebrow + title + standfirst + thin gold rule ───────────
// The Vogue/Tatler typographic chamber. Used both in centered and image-led
// variants. Sits BELOW the image, never on it.
function VTHeader({ eyebrow, title, standfirst, theme = 'dark', align = 'center' }) {
  const isLight = theme === 'light';
  const titleColor = isLight ? '#181410' : '#f5f0e8';
  const standColor = isLight ? 'rgba(24,20,16,0.68)' : 'rgba(245,240,232,0.74)';
  return (
    <div style={{
      maxWidth: 720,
      margin: '0 auto',
      padding: 'clamp(40px, 5vw, 72px) clamp(20px, 4vw, 32px) clamp(28px, 4vw, 48px)',
      textAlign: align,
    }}>
      {eyebrow && (
        <div style={{
          fontFamily: FU,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: GOLD,
          marginBottom: 18,
        }}>
          {eyebrow}
        </div>
      )}
      {title && (
        <h2 style={{
          fontFamily: FD,
          fontWeight: 400,
          fontStyle: 'italic',
          fontSize: 'clamp(30px, 4vw, 56px)',
          lineHeight: 1.08,
          letterSpacing: '-0.015em',
          color: titleColor,
          margin: '0 0 22px',
        }}>
          {title}
        </h2>
      )}
      {/* Thin gold rule, the single ornament VT-style sections allow */}
      <div aria-hidden style={{
        width: 36,
        height: 1,
        background: GOLD,
        opacity: 0.7,
        margin: align === 'center' ? '0 auto 24px' : '0 0 24px',
      }} />
      {standfirst && (
        <p style={{
          fontFamily: FS,
          fontWeight: 300,
          fontSize: 'clamp(15px, 1.4vw, 18px)',
          lineHeight: 1.65,
          color: standColor,
          margin: 0,
          maxWidth: align === 'center' ? 560 : 'none',
          marginLeft: align === 'center' ? 'auto' : 0,
          marginRight: align === 'center' ? 'auto' : 0,
        }}>
          {standfirst}
        </p>
      )}
    </div>
  );
}

// ─── Image, clean plate, no overlay. Optional caption underneath ───────────
function VTImage({ image, fullBleed = false, theme = 'dark' }) {
  if (!image?.src) return null;
  const isLight = theme === 'light';
  const captionColor = isLight ? 'rgba(24,20,16,0.55)' : 'rgba(245,240,232,0.55)';
  const isVideo = image.type === 'video' || /\.(mp4|webm|mov)(\?|$)/i.test(image.src || '');
  const cropAspect = image.crop || (fullBleed ? '21/9' : '4/5');

  return (
    <figure style={{
      margin: 0,
      ...(fullBleed
        ? { marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)' }
        : { maxWidth: 1100, margin: '0 auto' }),
    }}>
      <div style={{
        position: 'relative',
        width: '100%',
        aspectRatio: cropAspect.replace('/', ' / '),
        overflow: 'hidden',
        background: '#0a0908',
      }}>
        {isVideo ? (
          <video
            src={image.src}
            autoPlay loop muted playsInline
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover',
              objectPosition: image.focal || 'center',
              display: 'block',
            }}
          />
        ) : (
          <img
            src={imgUrl(image.src, { width: 1600 })}
            alt={image.alt || ''}
            loading="lazy"
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover',
              objectPosition: image.focal || 'center',
              display: 'block',
            }}
          />
        )}
      </div>
      {(image.caption || image.credit) && (
        <figcaption style={{
          fontFamily: FU,
          fontSize: 10,
          fontWeight: 500,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: captionColor,
          marginTop: 14,
          textAlign: 'center',
          padding: '0 24px',
        }}>
          {[image.caption, image.credit && `Photographed by ${image.credit}`]
            .filter(Boolean).join(' · ')}
        </figcaption>
      )}
    </figure>
  );
}

// ─── Body column, sans-serif, generous leading, narrow column ──────────────
function VTBody({ blocks = [], renderRightBlock }) {
  if (!blocks.length) return null;
  return (
    <div style={{
      maxWidth: 680,
      margin: '0 auto',
      padding: '0 clamp(20px, 4vw, 32px) clamp(48px, 7vw, 96px)',
      // Sans-serif body for VT layouts, restrained, contemporary.
      fontFamily: FU,
      fontSize: 'clamp(16px, 1.3vw, 17.5px)',
      lineHeight: 1.75,
      letterSpacing: '0.005em',
    }}>
      {blocks.map((block, i) => (
        <div key={block.id ?? i} style={{ marginBottom: 'clamp(16px, 2vw, 28px)' }}>
          {renderRightBlock ? renderRightBlock(block, i) : null}
        </div>
      ))}
    </div>
  );
}

// ─── Main VTSectionBlock ────────────────────────────────────────────────────
export default function VTSectionBlock({
  block,
  renderRightBlock,
  isMobile = false,
  theme = 'dark',
}) {
  const variant   = block?.variant || 'centered';
  const image     = block?.image   || {};
  const eyebrow   = block?.eyebrow || '';
  const title     = block?.title   || '';
  const standfirst = block?.standfirst || '';
  const body      = block?.body    || { blocks: [] };

  const reducedMotion = typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [fadeRef, isVisible] = useFadeIn(reducedMotion);
  const fadeStyle = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(12px)',
    transition: 'opacity 880ms cubic-bezier(0.2, 0.7, 0.2, 1), transform 880ms cubic-bezier(0.2, 0.7, 0.2, 1)',
    willChange: 'opacity, transform',
  };

  const sectionBg = theme === 'light' ? '#FAF8F4' : '#0a0908';

  // Variant: split-clean, desktop only. Mobile collapses to centered.
  if (variant === 'split-clean' && !isMobile) {
    return (
      <section
        ref={fadeRef}
        style={{
          padding: 'clamp(64px, 8vw, 128px) 0',
          background: sectionBg,
          ...fadeStyle,
        }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 'clamp(32px, 5vw, 96px)',
          maxWidth: 1280,
          margin: '0 auto',
          padding: '0 clamp(20px, 4vw, 56px)',
          alignItems: 'center',
        }}>
          <div>
            <VTImage image={image} fullBleed={false} theme={theme} />
          </div>
          <div>
            <VTHeader
              eyebrow={eyebrow}
              title={title}
              standfirst={standfirst}
              theme={theme}
              align="left"
            />
            <div style={{ marginTop: -16 }}>
              <VTBody blocks={body.blocks || []} renderRightBlock={renderRightBlock} />
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Variant: image-led, image full-bleed, header + body below in narrow column
  if (variant === 'image-led') {
    return (
      <section
        ref={fadeRef}
        style={{
          padding: 'clamp(48px, 7vw, 112px) 0',
          background: sectionBg,
          ...fadeStyle,
        }}
      >
        <VTImage image={image} fullBleed={true} theme={theme} />
        <VTHeader eyebrow={eyebrow} title={title} standfirst={standfirst} theme={theme} align="center" />
        <VTBody blocks={body.blocks || []} renderRightBlock={renderRightBlock} />
      </section>
    );
  }

  // Default: centered, image centered, header below, body below that.
  // Mobile fallback for split-clean lands here too.
  return (
    <section
      ref={fadeRef}
      style={{
        padding: 'clamp(48px, 7vw, 112px) 0',
        background: sectionBg,
        ...fadeStyle,
      }}
    >
      <VTImage image={image} fullBleed={false} theme={theme} />
      <VTHeader eyebrow={eyebrow} title={title} standfirst={standfirst} theme={theme} align="center" />
      <VTBody blocks={body.blocks || []} renderRightBlock={renderRightBlock} />
    </section>
  );
}
